import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Stack, Typography, Divider } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
// eslint-disable-next-line import/no-unresolved -- uuid v13 is exports-map only; the bundler resolves it fine
import { v4 as uuidv4 } from 'uuid';
import useThemeStyles from '../../../hooks/useThemeStyles';
import EntityCard from './EntityCard';
import RelationRow from './RelationRow';
import ErdDiagram from './ErdDiagram';

/**
 * ERD editor: structured entity/relation forms with a live diagram.
 */
const ErdSection = ({ entities, relations, onEntitiesChange, onRelationsChange }) => {
  const { colors } = useThemeStyles();

  const addEntity = () => {
    onEntitiesChange([
      ...entities,
      { id: uuidv4(), name: `Entity ${entities.length + 1}`, fields: [] },
    ]);
  };

  const updateEntity = (index, updated) => {
    onEntitiesChange(entities.map((entity, i) => (i === index ? updated : entity)));
  };

  const deleteEntity = (index) => {
    const removed = entities[index];
    onEntitiesChange(entities.filter((_, i) => i !== index));
    onRelationsChange(relations.filter((rel) => rel.from !== removed.id && rel.to !== removed.id));
  };

  const addRelation = () => {
    if (entities.length < 2) return;
    onRelationsChange([
      ...relations,
      {
        id: uuidv4(),
        from: entities[0].id,
        to: entities[1].id,
        type: 'one-many',
        label: '',
      },
    ]);
  };

  const updateRelation = (index, updated) => {
    onRelationsChange(relations.map((rel, i) => (i === index ? updated : rel)));
  };

  const deleteRelation = (index) => {
    onRelationsChange(relations.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <ErdDiagram entities={entities} relations={relations} />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle1" sx={{ color: colors.text.primary, fontWeight: 600 }}>
          Entities
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addEntity}
          sx={{ textTransform: 'none' }}
        >
          Add Entity
        </Button>
      </Box>

      {entities.length === 0 ? (
        <Typography variant="body2" sx={{ color: colors.text.secondary }}>
          Model your game data: items, characters, quests, inventory…
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 2,
          }}
        >
          {entities.map((entity, index) => (
            <EntityCard
              key={entity.id}
              entity={entity}
              onChange={(updated) => updateEntity(index, updated)}
              onDelete={() => deleteEntity(index)}
            />
          ))}
        </Box>
      )}

      <Divider />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle1" sx={{ color: colors.text.primary, fontWeight: 600 }}>
          Relations
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addRelation}
          disabled={entities.length < 2}
          sx={{ textTransform: 'none' }}
        >
          Add Relation
        </Button>
      </Box>

      {relations.length === 0 ? (
        <Typography variant="body2" sx={{ color: colors.text.secondary }}>
          {entities.length < 2
            ? 'Add at least two entities to create relations.'
            : 'No relations yet.'}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {relations.map((relation, index) => (
            <RelationRow
              key={relation.id}
              relation={relation}
              entities={entities}
              onChange={(updated) => updateRelation(index, updated)}
              onDelete={() => deleteRelation(index)}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

ErdSection.propTypes = {
  entities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  relations: PropTypes.arrayOf(PropTypes.shape({})),
  onEntitiesChange: PropTypes.func.isRequired,
  onRelationsChange: PropTypes.func.isRequired,
};

ErdSection.defaultProps = {
  entities: [],
  relations: [],
};

export default ErdSection;
